import { MagicVariables } from '@/types';
import { HTMXEvent } from '@/events/types';
import { redirect } from '@/utils/general';
import { sendGenericSearchEvent, sendSearchSuggestionSelectEvent } from '@/analytics/ga';
import { SearchSuggestionType } from '@/analytics/types';

import { FETCH_SEARCH_EVENT_NAME, MIN_SEARCH_LENGTH } from './config';

import { IJumpToBottom } from './jump_to_bottom';
import { IContentWrapper } from './content_wrapper';
import { IRecentSearches } from './recent_searches';
import { ILoadingHandler } from './loading_handler';
import { ISelectionHandler } from './selection_handler';
import { ITabs } from './tabs';

export class SearchBox implements MagicVariables {
    $root?: MagicVariables['$root'];
    $dispatch: MagicVariables['$dispatch'];
    $refs?: MagicVariables['$refs'];
    $nextTick?: MagicVariables['$nextTick'];

    jumpToBottom: IJumpToBottom;
    contentWrapper: IContentWrapper;
    recentSearches: IRecentSearches;
    loadingHandler: ILoadingHandler;
    selectionHandler: ISelectionHandler;
    tabs: ITabs;

    isMobile: boolean;
    hideExplicitContent = true;
    isBannedKWEntered = false;
    isLoading = false;
    isClearVisible = false;
    previousSearch = '';
    isMouseDown = false;

    constructor(
        isMobile: boolean,
        hideExplicitContent: boolean,
        jumpToBottom: IJumpToBottom,
        contentWrapper: IContentWrapper,
        recentSearches: IRecentSearches,
        loadingHandler: ILoadingHandler,
        selectionHandler: ISelectionHandler,
        tabs: ITabs
    ) {
        this.isMobile = isMobile;
        this.hideExplicitContent = hideExplicitContent;
        this.jumpToBottom = jumpToBottom;
        this.contentWrapper = contentWrapper;
        this.recentSearches = recentSearches;
        this.loadingHandler = loadingHandler;
        this.selectionHandler = selectionHandler;
        this.tabs = tabs;
    }

    init() {
        this.jumpToBottom.setContainer(this.$refs.searchResults);
        this.contentWrapper.setJumpToBottom(this.jumpToBottom);
        this.contentWrapper.setElements(this.$refs.searchResultsWrapper, this.$refs.searchResults);
        this.contentWrapper.openIfFocused();
        this.selectionHandler.setRootElement(this.$root);

        this.fetchSearchResults();
        this.setClearButtonVisibility();
        this.setSelectionChangeListener();
    }

    dispatchSearch() {
        this.$dispatch(FETCH_SEARCH_EVENT_NAME);
    }

    fetchSearchResults(keyword?: string) {
        // If no keyword is provided, API returns Trending categories
        // If keyword is provided, it should be minimum MIN_SEARCH_LENGTH characters long
        if (!keyword?.length || keyword.length >= MIN_SEARCH_LENGTH) {
            this.dispatchSearch();
        }

        this.previousSearch = keyword || '';
    }

    onInput() {
        const searchInputValue = this.getSearchInputValue();

        if (this.previousSearch === searchInputValue) {
            return;
        }

        this.fetchSearchResults(searchInputValue);
        this.setClearButtonVisibility();
    }

    setSelectionChangeListener() {
        // Selection change listener is only needed on desktop
        if (this.isMobile) {
            return;
        }

        // If the user starts searching by selecting text first before clicking the input...
        this.$refs.searchInput.addEventListener('selectionchange', () => {
            this.contentWrapper.open();
        });
    }

    openContentWrapper() {
        if (this.isMobile && !this.isInputFocused()) {
            this.focusSearch();
        }

        this.contentWrapper.open();
    }

    onMouseDown() {
        if (!this.isMobile) {
            this.isMouseDown = true;
        }

        this.openContentWrapper();
    }

    onMouseUp() {
        if (!this.isMobile) {
            this.isMouseDown = false;
        }
    }

    onSearchButtonClick() {
        const searchInputValue = this.getSearchInputValue();

        if (searchInputValue.length < MIN_SEARCH_LENGTH) {
            return;
        }

        this.handleSearchRedirection(searchInputValue);
        sendGenericSearchEvent(searchInputValue);
    }

    onSearchResultClick(keyword: string, type: SearchSuggestionType) {
        // An <a> tag is clicked, browser will redirect, but before that we save the searched keyword
        this.recentSearches.saveSearch(keyword);

        sendSearchSuggestionSelectEvent(type, keyword);
    }

    onClearClick() {
        this.setSearchInputValue('');
        this.setClearButtonVisibility();
        this.fetchSearchResults();
        this.selectionHandler.resetSelection();
    }

    onOutsideClick() {
        this.handleClose();
    }

    onCancelClick() {
        this.handleClose();
    }

    handleClose() {
        if (!this.isMouseDown) {
            this.contentWrapper.close();
        }

        if (!this.isMobile) {
            this.isMouseDown = false;
        }
    }

    handleBeforeRequest() {
        this.loadingHandler.startTimeout();
    }

    handleSuccessfulRequest(event: HTMXEvent) {
        this.isBannedKWEntered = !event.detail.xhr.response;

        this.jumpToBottom.setVisibility();
        this.recentSearches.refresh();
        this.loadingHandler.stopTimeout();

        if (this.isBannedKWEntered) {
            this.selectionHandler.resetSelection();
        }

        // If this $nextTick is removed, we can only go through the trending sections' selection.
        // That would happen because in the current call stack we have only instructed Alpine
        // to prepare to create the new DOM elements, but the DOM is not yet updated.
        // $nextTick will wait for the DOM elements to be created, and Alpine to create it's proxies on the elements.
        this.$nextTick(() => {
            this.selectionHandler.refresh();
        });
    }

    handleAfterRequest(event: HTMXEvent) {
        // If a new request is started before the previous has finished
        // HTMX will cancel the previous request. In this case xhr.status is 0.
        const isRequestAborted = event.detail.xhr.status === 0;

        if (isRequestAborted) {
            return;
        }

        this.handleSuccessfulRequest(event);
    }

    setClearButtonVisibility(): void {
        this.isClearVisible = this.getSearchInputValue().length >= MIN_SEARCH_LENGTH;
    }

    getSearchInputValue(): string {
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        return searchInput ? searchInput.value.trim() : '';
    }

    setSearchInputValue(value: string): void {
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        searchInput.value = value;
    }

    // Input focus

    focusSearch() {
        // Focus search and jump cursor to the end of the input
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        searchInput.focus();
        searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
        searchInput.scrollLeft = searchInput.scrollWidth;
    }

    clearSearchFocus() {
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        searchInput.blur();
    }

    isInputFocused(): boolean {
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        return searchInput && document.activeElement === searchInput;
    }

    isInputSameAsSelectedText(): boolean {
        const searchInputValue = this.getSearchInputValue();
        const activeElement = this.selectionHandler.getActiveElement();

        if (!activeElement) {
            return false;
        }

        return activeElement.innerText.trim().toLowerCase() === searchInputValue.toLowerCase();
    }

    onTabClick(tab: string) {
        this.tabs.setActive(tab);
        this.jumpToBottom.setVisibility();

        // When clicking a tab we change the DOM content, and Alpine at this point haven't discovered that.
        // $nextTick will wait for the DOM elements to be created, and Alpine to create it's proxies on the elements.
        this.$nextTick(() => {
            this.selectionHandler.onTabChange();
        });

        // On mobile it's distracting to re-open the virtual keyboard when tab is clicked,
        // and the user has more space for the results when the keyboard is hidden.
        // This is the opposite on desktop, where the keyboard is not shown at all.
        if (!this.isMobile) {
            this.focusSearch();
        }
    }

    // Keyboard events

    handleSearchByEnterPress() {
        const searchInputValue = this.getSearchInputValue();
        const keyboardSelectedElement = this.selectionHandler.getActiveElement();

        // Enter was pressed when an item was selected
        if (keyboardSelectedElement && this.isInputSameAsSelectedText()) {
            const keyword = this.selectionHandler.getSelectedItemText();

            this.recentSearches.saveSearch(keyword);
            this.redirectByKeyboardSelectedItem(keyboardSelectedElement);

            const suggestionType = this.getSearchSuggestionType(keyboardSelectedElement);
            sendSearchSuggestionSelectEvent(suggestionType, keyword);

            return;
        }

        // Enter was pressed when no item was selected
        this.handleSearchRedirection(searchInputValue);
        sendGenericSearchEvent(searchInputValue);
    }

    onEnterPress() {
        const searchInputValue = this.getSearchInputValue();

        if (searchInputValue.length < MIN_SEARCH_LENGTH) {
            return;
        }

        this.handleSearchByEnterPress();
    }

    handleHorizontalPress() {
        const searchInputValue = this.getSearchInputValue();

        if (this.isInputSameAsSelectedText() && this.previousSearch !== searchInputValue) {
            this.fetchSearchResults(searchInputValue);
        }

        this.setClearButtonVisibility();
    }

    handleVerticalPress() {
        this.setSearchInputValue(this.selectionHandler.getSelectedItemText());
        this.setClearButtonVisibility();
    }

    onLeftPress() {
        this.handleHorizontalPress();
    }

    onRightPress() {
        this.handleHorizontalPress();
    }

    onUpPress(event: KeyboardEvent) {
        // Prevent jumping the caret to the first character of the input
        event.preventDefault();

        this.selectionHandler.selectPrevious();
        this.handleVerticalPress();
        this.focusSearch();
    }

    onDownPress() {
        this.selectionHandler.selectNext();
        this.handleVerticalPress();
    }

    onEscPress() {
        this.contentWrapper.close();
        this.clearSearchFocus();
    }

    // Redirection

    handleSearchRedirection(keyword: string) {
        this.recentSearches.saveSearch(keyword);
        this.redirectToSearchPage(keyword);
    }

    redirectByKeyboardSelectedItem(element: HTMLAnchorElement) {
        if (element.href) {
            redirect(element.href);
        } else {
            this.redirectToSearchPage(this.selectionHandler.getSelectedItemText());
        }
    }

    redirectToSearchPage(keyword: string) {
        redirect('/s/' + this.formatSearchText(keyword) + '/');
    }

    // Analytics

    getSearchSuggestionType(element: HTMLAnchorElement): SearchSuggestionType {
        return element.dataset.category as SearchSuggestionType;
    }

    formatSearchText(text: string): string {
        return text
            .replace(/[#&\-!$%()*,/:;?@\[\]"¨“”‘´^_{|}~¡¿<>=.¦¸¥₽«»×§°·⁄]/g, ' ')
            .replace(/([ '`’])\1+/g, '$1')
            .trim()
            .replace(/ /g, '+');
    }
}
